import { useSyncExternalStore } from 'react';
import type { AgeBand } from '@/game/coach/phrases';

/**
 * Grown-up settings kept on this device: the player's age. The age chooses the coach's phrasing
 * and session length (5-7 vs 8 and up) and how strict the scanner is (see `youngVoice`).
 */
export interface Settings {
  /** Age in years (13 means 13 or older); null until a grown-up sets it. */
  age: number | null;
}

export const AGES: readonly number[] = [5, 6, 7, 8, 9, 10, 11, 12, 13];

export function ageBand(age: number | null): AgeBand {
  return age !== null && age >= 8 ? 'older' : 'younger';
}

/**
 * Scored with the threshold for young voices (8 and under). On clinician-rated children aged 6-8
 * the recognizer is less sure of a correct /ɹ/; see docs/speech.md "Young children". Unknown age
 * stays strict.
 */
export function youngVoice(age: number | null): boolean {
  return age !== null && age <= 8;
}

const KEY = 'foe-names.settings.v1';

function load(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    const age = raw ? Number((JSON.parse(raw) as Partial<Settings>).age) : NaN;
    return { age: AGES.includes(age) ? age : null };
  } catch {
    return { age: null };
  }
}

let settings: Settings = load();
const listeners = new Set<() => void>();

export const settingsStore = {
  get: (): Settings => settings,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  setAge: (age: number) => {
    settings = { ...settings, age };
    try {
      localStorage.setItem(KEY, JSON.stringify(settings));
    } catch {
      // Storage blocked: the setting lasts for this visit.
    }
    for (const l of listeners) l();
  },
};

export function useSettings(): Settings {
  return useSyncExternalStore(settingsStore.subscribe, settingsStore.get);
}
